import {Subject} from 'rxjs';
import {numUtil} from '@/helper/num';
import {timeUtil} from '@/helper/time';
import {marginTradeApi} from '@/api/margin-trade';
import {mc} from '@/core';
import {TRADING_ACCOUNT_TYPE} from '@/modules/assets/map';
import {typeMap as RepayTypeMap, BusinessMap as RepayBusinessMap} from '@/modules/assets/margin-trade/components/repay-history/stream';
import {typeMap as BorrowTypeMap, BusinessMap as BorrowBusinessMap} from '@/modules/assets/margin-trade/components/borrow-history/stream';
import rLang from '@/modules/assets/margin-trade/components/repay-history/RepayHistoryPage.lang';
import bLang from '@/modules/assets/margin-trade/components/borrow-history/BorrowHistoryPage.lang';

export const timeRangeInfo$ = new Subject();

export async function loadMarginTradeDetails(query) {
  return await loadMarginFundDetails(query, 0, 0);
}

export async function loadTradingAccountDetails(query) {
  return await loadMarginFundDetails(query, 1, 0);
}

export async function loadFundAccountDetails(query) {
  return await loadMarginFundDetails(query, 1, 1);
}

export async function loadMarginTradeHistory(query) {
  const {currency, end, start, limit, utaState, business, offset} = query;
  const pageNum = Math.floor(offset / limit) + 1;
  const newQuery = {
    currency,
    pageNum,
    pageSize: limit,
    end,
    start,
    utaState,
    business,
  };
  const {data, error} = await marginTradeApi.getTradeHistory(newQuery);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

export function genMarginFundDetails(rows, {currencies, LG}) {
  if (currencies.length < 1) {
    return null;
  }
  return rows.map((item) => {
    const {transactTimeNs, currency, bizCode, amountRv, balanceRv} = item;
    const time = timeUtil.formatDateTime(transactTimeNs * 1e-6);
    const curCurrency = getCurrency(currencies, currency);
    const valuePrecision = curCurrency ? curCurrency.valuePrecision : 8;
    const changeAmount = (Number(amountRv) > 0 ? '+' : '') + numUtil.floor(amountRv, valuePrecision) + ' ' + currency;
    const balance = numUtil.floor(balanceRv, valuePrecision) + ' ' + currency;
    return [time, currency, TRADING_ACCOUNT_TYPE(LG)[bizCode], changeAmount, balance];
  });
}

export function genMarginTradeExportList(rows, {spots, currencies}) {
  if (spots.length < 1) {
    return null;
  }

  return rows.map((item) => {
    const {side, transactTimeNs, ordType, execId, qtyType, feeCurrency, execBaseQtyRq, execQuoteQtyRq, execPriceRp, baseQtyRq, quoteQtyRq, priceRp, feeRateRr, execFeeRv} = item;
    const time = timeUtil.formatUTCDateTime(transactTimeNs * 1e-6);
    const spot = getSpot(spots, item);
    if (!spot) {
      return [time, '--', side, '--', '--', '--', '--', '--', '--', '--', ordType, execId.substring(0, 8)];
    }
    const {displaySymbol, pricePrecision, displayBaseCurrency, baseQtyPrecision, quoteCurrency, quoteQtyPrecision} = spot;
    const amountReceived = side === 'Buy' ? numUtil.floor(execBaseQtyRq, baseQtyPrecision) + ' ' + displayBaseCurrency : numUtil.floor(execQuoteQtyRq, quoteQtyPrecision) + ' ' + quoteCurrency;
    const value = numUtil.floor(execPriceRp, pricePrecision);
    const amountSpent = side === 'Buy' ? numUtil.floor(execQuoteQtyRq, quoteQtyPrecision) + ' ' + quoteCurrency : numUtil.floor(execBaseQtyRq, baseQtyPrecision) + ' ' + displayBaseCurrency;
    const amount = qtyType === 'ByBase' ? numUtil.floor(baseQtyRq, baseQtyPrecision) + ' ' + displayBaseCurrency : numUtil.floor(quoteQtyRq, quoteQtyPrecision) + ' ' + quoteCurrency;
    const price = ordType === 'Market' ? 'Market' : numUtil.floor(priceRp, pricePrecision);
    const curCurrency = getCurrency(currencies, feeCurrency);
    if (!curCurrency) {
      return [time, displaySymbol, side, amountReceived, value, amountSpent, amount, price, '--', '--', ordType, execId.substring(0, 8)];
    }
    const {valuePrecision, displayCurrency} = curCurrency;
    const feeRate = numUtil.floor(feeRateRr, 6, -2) + '%';
    const feePaid = numUtil.floor(execFeeRv, valuePrecision) + ' ' + displayCurrency;
    return [time, displaySymbol, side, amountReceived, value, amountSpent, amount, price, feeRate, feePaid, ordType, execId.substring(0, 8)];
  });
}
async function loadMarginFundDetails(query, utaState = 0, targetFund = 0) {
  const {currency, end, start, limit, offset} = query;
  const pageNum = Math.floor(offset / limit) + 1;
  const newQuery = {
    currency,
    pageNum,
    pageSize: limit,
    end,
    start,
    utaState,
    targetFund,
  };
  const {data, error} = await marginTradeApi.getFundsHistory(newQuery);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

export function getSpot(spots, row) {
  return spots.find((o) => o.symbol === row.symbol);
}

export function getCurrency(currencies, currency) {
  return currencies.find((o) => o.currency === currency);
}

export function createGenMarginTradeBorrowHistoryFun(isUpgraded) {
  return function (rows, {currencies, LG}) {
    if (currencies.length < 1) {
      return null;
    }
    return rows.map((o) => {
      const curCurrency = getCurrency(currencies, o.currency);
      const valuePrecision = curCurrency ? curCurrency.valuePrecision : 8;
      const amount = numUtil.floor(o.amountRv, valuePrecision, 0);
      return isUpgraded
        ? [o.currency, LG(bLang[BorrowBusinessMap[o.business + '']]), timeUtil.formatDateTime(o.borrowTime), amount, LG(bLang[BorrowTypeMap[o.type + '']]), o.status]
        : [o.currency, timeUtil.formatDateTime(o.borrowTime), amount, LG(bLang[BorrowTypeMap[o.type + '']]), o.status];
    });
  };
}

export function createGenMarginTradeRepayHistoryFun(isUpgraded) {
  return function (rows, {currencies, LG}) {
    if (currencies.length < 1) {
      return null;
    }
    return rows.map((o) => {
      const curCurrency = getCurrency(currencies, o.currency);
      const valuePrecision = curCurrency ? curCurrency.valuePrecision : 8;
      const totalRepayAmountNum = Number(o.principalAmountRv) + Number(o.interestAmountRv);
      const principalAmount = numUtil.floor(o.principalAmountRv, valuePrecision);
      const interestAmount = numUtil.floor(o.interestAmountRv, valuePrecision);
      const totalRepayAmount = numUtil.floor(totalRepayAmountNum, valuePrecision);
      const dateTime = timeUtil.formatDateTime(o.repayTime);
      return isUpgraded
        ? [o.currency, LG(rLang[RepayBusinessMap[o.business + '']]), dateTime, principalAmount, interestAmount, totalRepayAmount, LG(rLang[RepayTypeMap[o.type + '']]), o.status]
        : [o.currency, dateTime, principalAmount, interestAmount, totalRepayAmount, LG(rLang[RepayTypeMap[o.type + '']]), o.status];
    });
  };
}

export async function loadMarginTradeRepayHistory(query) {
  const {currency, end, start, limit, offset} = query;
  const pageNum = Math.floor(offset / limit) + 1;
  const newQuery = {
    currency,
    pageNum,
    pageSize: limit,
    end,
    start,
  };
  const {data, error} = await marginTradeApi.getRepayHistory(newQuery);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

export async function loadMarginOrderHistory(query) {
  const {currency, end, start, limit, business, utaState, offset} = query;
  const pageNum = Math.floor(offset / limit) + 1;
  const newQuery = {
    currency,
    pageNum,
    pageSize: limit,
    end,
    start,
    utaState,
    business,
  };
  const {data, error} = await marginTradeApi.getOrderHistory(newQuery);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}
